/** Observed paid purchase history; identities and denominators are product/customer/order scoped. */
import * as z from 'zod';
import { insightAnswerMetadataSchema } from './insight-answer.js';
export const productHistoryDefinition = {
    id: 'product-history',
    owner: 'shopify',
    basis: 'current_observed_paid_product_history',
    unsupportedClaims: [
        'Collected purchases are not complete lifetime history.',
        'Observed timing is not an optimal reminder schedule or retention rate.',
        'Next means next observed qualifying paid order, not first-ever product purchase.',
        'Product customer counts overlap and cannot be summed.',
        'Positive retained quantity and price are not accounting net revenue.',
    ],
};
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const stamp = z.iso.datetime({ offset: true }).nullable();
export const productHistoryCoverageSchema = z.strictObject({
    totalOrders: count,
    eligibleOrders: count,
    excludedOrders: count,
    unknownOrders: count,
    currentLines: count,
    eligibleLines: count,
    unknownLines: count,
    excludedLines: count,
    missingLineOrders: count,
    paidOrders: count,
    missingCustomers: count,
    missingDates: count,
    tiedOrders: count,
    orderedOrders: count,
    customers: count,
    identifiedCustomers: count,
    knownDateOrders: count,
    indexReversals: count,
    transitions: count,
    transitionCustomers: count,
    sequenceGaps: count,
    unknownIndexes: count,
    firstToSecond: count,
    recurrenceProducts: count,
    transitionPairs: count,
    repeatCustomers: count,
    firstDate: stamp,
    lastDate: stamp,
    ordersSyncedAt: stamp,
    linesSyncedAt: stamp,
});
export const productHistoryEntitySchema = z.strictObject({
    productId: z.string().regex(/^[\x21-\x7e]+$/),
    title: z.string().nullable(),
    imageUrl: z.string().nullable(),
});
const timing = z
    .strictObject({
    intervals: count,
    medianDays: z.number().finite().nonnegative().nullable(),
    minDays: z.number().finite().nonnegative().nullable(),
    maxDays: z.number().finite().nonnegative().nullable(),
})
    .superRefine((t, c) => {
    if (t.intervals === 0
        ? t.medianDays !== null || t.minDays !== null || t.maxDays !== null
        : t.minDays === null ||
            t.medianDays === null ||
            t.maxDays === null ||
            t.minDays > t.medianDays ||
            t.medianDays > t.maxDays)
        c.addIssue({ code: 'custom', message: 'INSIGHT_PRESENTATION_INVALID: interval summary' });
});
export const productRecurrenceSchema = z.strictObject({
    product: productHistoryEntitySchema,
    buyers: count,
    followupBuyers: count,
    customers: count,
    occasions: count,
    timing,
});
export const productNextOrderSchema = z.strictObject({
    earlier: productHistoryEntitySchema,
    next: productHistoryEntitySchema,
    customers: count,
    occasions: count,
    opportunityCustomers: count,
    opportunities: count,
    absentFromEarlier: count,
    timing,
});
export const productHistoryInputsSchema = z
    .strictObject({
    coverage: productHistoryCoverageSchema,
    recurrence: z.array(productRecurrenceSchema),
    nextOrder: z.array(productNextOrderSchema),
})
    .superRefine((v, ctx) => {
    const c = v.coverage;
    let valid = c.totalOrders === c.eligibleOrders + c.excludedOrders + c.unknownOrders &&
        c.paidOrders <= c.eligibleOrders - c.missingLineOrders &&
        c.paidOrders <= c.eligibleLines &&
        c.eligibleLines + c.unknownLines + c.excludedLines <= c.currentLines &&
        c.orderedOrders + c.missingCustomers + c.missingDates + c.tiedOrders === c.paidOrders &&
        c.customers <= c.orderedOrders &&
        c.orderedOrders <= c.knownDateOrders &&
        c.knownDateOrders <= c.paidOrders &&
        c.transitions <= c.orderedOrders - c.customers &&
        c.transitions >= Math.max(0, c.orderedOrders - c.customers - c.tiedOrders) &&
        c.transitionCustomers <= Math.min(c.customers, c.transitions) &&
        c.sequenceGaps + c.unknownIndexes + c.indexReversals <= c.transitions &&
        c.firstToSecond <= c.transitions &&
        (c.repeatCustomers === 0) === (c.recurrenceProducts === 0) &&
        c.repeatCustomers <= c.identifiedCustomers &&
        c.identifiedCustomers <= c.paidOrders - c.missingCustomers &&
        c.customers <= c.identifiedCustomers &&
        v.recurrence.length === c.recurrenceProducts &&
        v.nextOrder.length === c.transitionPairs;
    valid &&=
        (c.totalOrders === 0) === (c.ordersSyncedAt === null) &&
            (c.currentLines === 0) === (c.linesSyncedAt === null) &&
            (c.knownDateOrders === 0) === (c.firstDate === null && c.lastDate === null) &&
            (c.knownDateOrders === 0 ||
                (c.firstDate !== null && c.lastDate !== null && Date.parse(c.firstDate) <= Date.parse(c.lastDate)));
    const keys = new Set();
    let prev;
    const ordered = (customers, occasions, key) => {
        const ok = !keys.has(key) &&
            (!prev ||
                prev.customers > customers ||
                (prev.customers === customers &&
                    (prev.occasions > occasions || (prev.occasions === occasions && prev.key < key))));
        keys.add(key);
        prev = { customers, occasions, key };
        return ok;
    };
    for (const r of v.recurrence)
        valid &&=
            ordered(r.customers, r.occasions, r.product.productId) &&
                r.customers > 0 &&
                r.customers <= r.buyers &&
                r.followupBuyers <= r.buyers &&
                r.buyers <= c.identifiedCustomers &&
                r.customers <= c.repeatCustomers &&
                r.occasions >= r.customers &&
                r.occasions <= c.paidOrders - c.identifiedCustomers &&
                r.timing.intervals <= r.occasions;
    keys.clear();
    prev = undefined;
    for (const r of v.nextOrder)
        valid &&=
            ordered(r.customers, r.occasions, r.earlier.productId + '\0' + r.next.productId) &&
                r.earlier.productId !== r.next.productId &&
                r.customers > 0 &&
                r.customers <= r.opportunityCustomers &&
                r.opportunityCustomers <= c.transitionCustomers &&
                r.opportunities <= c.transitions &&
                r.opportunityCustomers <= r.opportunities &&
                r.occasions >= r.customers &&
                r.occasions <= r.opportunities &&
                r.absentFromEarlier <= r.occasions &&
                r.timing.intervals === r.occasions;
    if (!valid)
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: paid history populations, identities or ordering',
        });
});
export function productHistoryFacts(input) {
    const facts = productHistoryInputsSchema.parse(input), c = facts.coverage;
    let reason = null;
    if (c.totalOrders === 0)
        reason = 'no_store_data';
    else if (c.paidOrders === 0 && c.missingLineOrders > 0)
        reason = 'source_incomplete';
    else if (c.paidOrders === 0 && c.unknownOrders + c.unknownLines > 0)
        reason = 'eligibility_unknown';
    else if (c.paidOrders > 0 && c.identifiedCustomers === 0)
        reason = 'history_unordered';
    return { state: reason === null ? 'ready' : 'unavailable', reason, ...facts };
}
export const productHistoryAnswerSchema = z
    .strictObject({
    ...insightAnswerMetadataSchema.shape,
    definition: z.strictObject({ id: z.literal('product-history') }),
    state: z.enum(['ready', 'unavailable']),
    reason: z.enum(['no_store_data', 'source_incomplete', 'eligibility_unknown', 'history_unordered']).nullable(),
    ...productHistoryInputsSchema.shape,
    actions: z.tuple([]),
})
    .superRefine((answer, ctx) => {
    const input = productHistoryInputsSchema.safeParse({
        coverage: answer.coverage,
        recurrence: answer.recurrence,
        nextOrder: answer.nextOrder,
    });
    const timestamps = answer.evidence.sourceTimestamps;
    const valid = input.success &&
        answer.evidence.basis === productHistoryDefinition.basis &&
        timestamps.length === 2 &&
        timestamps[0]?.source === 'orders' &&
        timestamps[0]?.syncedAt === answer.coverage.ordersSyncedAt &&
        timestamps[1]?.source === 'order_lines' &&
        timestamps[1]?.syncedAt === answer.coverage.linesSyncedAt &&
        JSON.stringify(productHistoryFacts(input.data)) ===
            JSON.stringify({
                state: answer.state,
                reason: answer.reason,
                coverage: answer.coverage,
                recurrence: answer.recurrence,
                nextOrder: answer.nextOrder,
            });
    if (!valid)
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: product-history answer differs from facts or evidence',
        });
});
//# sourceMappingURL=product-history-answer.js.map